import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { LibraryNavigation } from "../components/navigation/LibraryNavigation";
import { toast } from "sonner";
import { 
  Sparkles, Star, Phone, Users, Calendar, Clock, MapPin, 
  ArrowLeft, CheckCircle, Video, Timer
} from "lucide-react";
import { Button } from "../components/ui/button";

const MyExperiences = () => {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    fetchBookings();
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/experiences/booked`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(res.data);
    } catch (e) {
      console.error("Failed to fetch bookings:", e);
    } finally {
      setLoading(false);
    }
  };

  const getCountdown = (bookedAt) => {
    // For demo, countdown from booking time + 7 days
    const booked = new Date(bookedAt);
    const eventDate = new Date(booked.getTime() + 7 * 24 * 60 * 60 * 1000);
    const diff = eventDate.getTime() - now;
    
    if (diff <= 0) return { text: "Happening now!", urgent: true, passed: false };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    if (days > 0) return { text: `${days}d ${hours}h ${minutes}m`, urgent: days < 1, passed: false };
    if (hours > 0) return { text: `${hours}h ${minutes}m ${seconds}s`, urgent: true, passed: false };
    return { text: `${minutes}m ${seconds}s`, urgent: true, passed: false };
  };

  const typeLabels = { fan_meet: "Supporter Meet", video_call: "1-on-1 Video Call", backstage: "Backstage Access" };
  const typeIcons = { fan_meet: Star, video_call: Phone, backstage: Users };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="my-experiences-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Link to="/dashboard">
            <Button className="bg-[#0A0E18] border border-[#1A2030] text-white hover:bg-[#1A2030]" data-testid="back-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-20 px-6">
        <LibraryNavigation />
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2" data-testid="my-exp-title">My Experiences</h1>
            <p className="text-[#8A92A0]">Your booked fan meets, video calls, and backstage access</p>
          </div>

          {bookings.length === 0 ? (
            <div className="card p-16 text-center">
              <Star className="w-16 h-16 text-[#8A92A0] mx-auto mb-4" />
              <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-2">No experiences booked yet</h2>
              <p className="text-[#8A92A0] mb-6">Browse artists and book exclusive experiences!</p>
              <Link to="/fandom">
                <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="browse-artists-btn">
                  <Users className="w-4 h-4 mr-2" /> Explore Artists
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => {
                const exp = booking.experience;
                const artist = booking.artist;
                const countdown = getCountdown(booking.booked_at);
                const TypeIcon = typeIcons[exp?.experience_type] || Star;
                
                return (
                  <div key={booking.booking_id} className="card p-6 relative overflow-hidden" data-testid={`booking-${booking.booking_id}`}>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D14A85] to-[#EC008C]" />
                    
                    <div className="flex flex-col md:flex-row items-start gap-6">
                      {/* Left - Experience Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-xl bg-[#D14A85]/10 flex items-center justify-center">
                            <TypeIcon className="w-6 h-6 text-[#D14A85]" />
                          </div>
                          <div>
                            <span className="text-xs font-medium text-[#EC008C] uppercase tracking-wider">
                              {typeLabels[exp?.experience_type] || "Experience"}
                            </span>
                            <h3 className="font-['Unbounded'] text-lg font-semibold text-white">
                              {exp?.title || "Experience"}
                            </h3>
                          </div>
                        </div>
                        
                        {artist && (
                          <Link to={`/artist/${artist.artist_id}`} className="inline-flex items-center gap-2 mb-3 text-[#8A92A0] hover:text-[#EC008C] transition-colors">
                            {artist.profile_image ? (
                              <img src={artist.profile_image} alt="" className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-[#1A2030]" />
                            )}
                            <span className="text-sm font-medium">{artist.stage_name}</span>
                          </Link>
                        )}
                        
                        {exp?.description && (
                          <p className="text-[#8A92A0] text-sm mb-3">{exp.description}</p>
                        )}
                        
                        <div className="flex items-center gap-4 text-sm text-[#8A92A0]">
                          {exp?.duration_minutes && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" /> {exp.duration_minutes} min
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" /> Booked {new Date(booking.booked_at).toLocaleDateString()}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            booking.status === "confirmed" 
                              ? "bg-[#8DB87A]/10 text-[#8DB87A]" 
                              : "bg-[#A1A1AA]/10 text-[#8A92A0]"
                          }`}>
                            {booking.status === "confirmed" ? "Confirmed" : booking.status}
                          </span>
                        </div>

                        {/* Join meeting CTA — visible as soon as the artist has set a URL */}
                        {(booking.meeting_url || exp?.meeting_url) && (
                          <div className="mt-4 p-3 rounded-xl bg-[#EC008C]/5 border border-[#EC008C]/30" data-testid={`join-meeting-block-${booking.booking_id}`}>
                            {booking.scheduled_at || exp?.scheduled_at ? (
                              <p className="text-xs text-[#8A92A0] mb-2">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                Scheduled for {new Date(booking.scheduled_at || exp?.scheduled_at).toLocaleString()}
                              </p>
                            ) : null}
                            {booking.instructions || exp?.instructions ? (
                              <p className="text-xs text-[#8A92A0] mb-2">📝 {booking.instructions || exp?.instructions}</p>
                            ) : null}
                            <a
                              href={booking.meeting_url || exp?.meeting_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#EC008C] text-black hover:bg-[#FF3399] font-semibold text-sm transition-colors"
                              data-testid={`join-meeting-btn-${booking.booking_id}`}
                            >
                              <Video className="w-4 h-4" />
                              Join meeting
                            </a>
                            <span className="ml-3 text-xs text-[#6A7280]">
                              via {(booking.meeting_provider || exp?.meeting_provider || "link").replace("_", " ")}
                            </span>
                          </div>
                        )}
                        {!(booking.meeting_url || exp?.meeting_url) && (
                          <p className="mt-3 text-xs text-[#8A92A0] italic">
                            ⏳ The artist hasn&apos;t set the meeting link yet. You&apos;ll see a &quot;Join&quot; button here once they do.
                          </p>
                        )}
                      </div>
                      
                      {/* Right - Countdown Timer */}
                      <div className="text-center md:text-right flex-shrink-0">
                        <div className={`inline-flex items-center gap-2 px-4 py-3 rounded-xl ${
                          countdown.urgent 
                            ? "bg-[#D14A85]/10 border border-[#D14A85]/30" 
                            : "bg-[#EC008C]/5 border border-[#EC008C]/20"
                        }`}>
                          <Timer className={`w-5 h-5 ${countdown.urgent ? "text-[#D14A85] animate-pulse" : "text-[#EC008C]"}`} />
                          <div>
                            <p className="text-xs text-[#8A92A0] uppercase tracking-wider">Countdown</p>
                            <p className={`font-['Unbounded'] text-lg font-bold ${
                              countdown.urgent ? "text-[#D14A85]" : "text-[#EC008C]"
                            }`}>
                              {countdown.text}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default MyExperiences;
